import React, { useState, useEffect } from 'react';
import { Grid, Collapse, Box } from '@mui/material';
import {
    Select,
    DateInput,
    Input,
    WeightInput,
    SearchableDropdown,
    InformationAlert,
    BinaryButtonGroup
} from '../../Common';
import Enums from '../../../enums';
import { yes_no_options, } from '../utils';
import { Translate, TranslateW } from "../../../Utils/Languages/Translate";
import LeakLocationAutocomplete from './LeakLocationAutocomplete';

const RepairDetails = ({
    formState,
    formState: {
        workItemTypeId: serviceEventTypeId,
        coolingApplianceStatusId,
    },
    dropdownOptions,
    setGlobalState,
    setRequiredFields,
    requiredFieldsMissing,
    requiredFieldsMissing: {
        initialLeakInspection: initialLeakInspectionErrors = {},
        maintenanceNoGas: maintenanceNoGasErrors = {},
    },
    serviceEventSteps,
    setServiceEventSteps,
    fieldsWithErrors: {
        dateLeakFound: {
            errorExists = false,
            helperText = "",
        } = {}
    },
    requiredFieldsRef,
}) => {
    const [warning, setWarning] = useState(false);
    const isServiceOrLeakRepair = serviceEventTypeId === Enums.SE.WorkItemType.ServiceOrLeakRepair;
    const isLeakInspection = serviceEventTypeId === Enums.SE.WorkItemType.LeakInspection;
    const isRecordRefrigerant = serviceEventTypeId === Enums.SE.WorkItemType.RecordRefrigerant;
    const isMaintenanceNoGas = serviceEventTypeId === Enums.SE.WorkItemType.MaintenanceNoGas;
    const wasLeakFound = !!formState.initialLeakInspection.wasLeakFound;
    const isNestedProperty = true;
    const defaultHelperText = "This field is required";

    // Clear conditional leak values through the state setter (not `delete formState.x`) so the
    // cleared values are committed and re-rendered. Raw mutation bypassed setGlobalState, so the
    // fields kept stale data and reappeared when toggling "Was a leak found?" back to Yes (ADO #15215).
    const deleteInitialLeakInspectionValues = () => {
        setGlobalState("initialLeakInspection.inspectionDate", null, isNestedProperty);
        setGlobalState("initialLeakInspection.leakLocationCategoryId", null, isNestedProperty);
        setGlobalState("initialLeakInspection.leakLocationId", null, isNestedProperty);
        setGlobalState("initialLeakInspection.faultCauseTypeId", null, isNestedProperty);
        setGlobalState("initialLeakInspection.estimatedLeakAmount", null, isNestedProperty);
    };

    const deleteAllLeakRelatedValues = () => {
        deleteInitialLeakInspectionValues();
        setGlobalState("repairOutcomeTypeId", null);

        // Clear initialVerificationLeakInspection fields (from InitialVerificationTest step)
        setGlobalState("initialVerificationLeakInspection.inspectionDate", null, isNestedProperty);
        setGlobalState("initialVerificationLeakInspection.wasLeakFound", null, isNestedProperty);
        setGlobalState("initialVerificationLeakInspection.leakDetectionMethodId", null, isNestedProperty);

        // Clear verificationLeakInspection fields (from FollowUpVerificationService step)
        setGlobalState("verificationLeakInspection.leakRepairDispositionTypeId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.leakDetectionMethodId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.wasLeakFound", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.leakLocationCategoryId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.leakLocationId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.faultCauseTypeId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.estimatedLeakAmount", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.notes", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.inspectionDate", null, isNestedProperty);

        // Clear follow-up service date
        setGlobalState("dateOfFollowUpService", null);

        // Remove InitialVerificationTest and FollowUpVerificationService steps if present
        const mappedSteps = serviceEventSteps.map(s => s.props.id);
        let newSteps = [...serviceEventSteps];
        let stepsChanged = false;

        const followUpIndex = mappedSteps.indexOf('FollowUpVerificationService');
        if (followUpIndex !== -1) {
            newSteps.splice(followUpIndex, 1);
            stepsChanged = true;
        }

        const initialVerifIndex = newSteps.map(s => s.props.id).indexOf('InitialVerificationTest');
        if (initialVerifIndex !== -1) {
            newSteps.splice(initialVerifIndex, 1);
            stepsChanged = true;
        }

        if (stepsChanged) {
            setServiceEventSteps(newSteps);
        }
    };

    const deleteIvtAndFvtRelatedValues = () => {
        setGlobalState("initialVerificationLeakInspection.inspectionDate", null, isNestedProperty);
        setGlobalState("initialVerificationLeakInspection.wasLeakFound", null, isNestedProperty);
        setGlobalState("initialVerificationLeakInspection.leakDetectionMethodId", null, isNestedProperty);

        setGlobalState("verificationLeakInspection.leakRepairDispositionTypeId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.leakDetectionMethodId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.wasLeakFound", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.leakLocationCategoryId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.leakLocationId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.faultCauseTypeId", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.estimatedLeakAmount", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.notes", null, isNestedProperty);
        setGlobalState("verificationLeakInspection.inspectionDate", null, isNestedProperty);

        setGlobalState("dateOfFollowUpService", null);

        // Remove FVT step from stepper since it no longer applies after switching outcome.
        const followUpIndex = serviceEventSteps.map(s => s.props.id).indexOf('FollowUpVerificationService');
        if (followUpIndex !== -1) {
            const newSteps = [...serviceEventSteps];
            newSteps.splice(followUpIndex, 1);
            setServiceEventSteps(newSteps);
        }

        const resetRequiredFields = {
            ...requiredFieldsRef.current,
            InitialVerificationTest: [],
            FollowUpVerificationService: [],
        };
        requiredFieldsRef.current = resetRequiredFields;
        setRequiredFields(resetRequiredFields);
    };

    const handleRepairOutcomeChange = (newOutcomeId) => {
        setGlobalState("repairOutcomeTypeId", newOutcomeId);

        if (newOutcomeId === Enums.SE.RepairOutcomeType.NoRepairAttempted) {
            deleteIvtAndFvtRelatedValues();
            return;
        }

        if (
            newOutcomeId === Enums.SE.RepairOutcomeType.LeakRepaired ||
            newOutcomeId === Enums.SE.RepairOutcomeType.LeakNotRepaired
        ) {
            const newRequiredFields = {
                ...requiredFieldsRef.current,
                InitialVerificationTest: [
                    "leakDetectionMethodId",
                    "wasLeakFound",
                ],
            };
            requiredFieldsRef.current = newRequiredFields;
            setRequiredFields(newRequiredFields);
        }
    };

    const setConditionalRequiredFields = (leakFound, nestedProperty) => {
        const typesWithConditionalFields = [2, 3]; // serviceEvent type IDs with conditional fields
        // Service or Leak Repair
        if (typesWithConditionalFields.includes(serviceEventTypeId)) {
            if (leakFound === true) { // leak found, add additional required fields
                const repairDetailsFields = [
                    "leakDetectionMethodId",
                    "wasLeakFound",
                    "inspectionDate",
                    "leakLocationCategoryId",
                    "leakLocationId",
                    "faultCauseTypeId",
                ];

                const newRequiredFields = {
                    ...requiredFieldsRef.current,
                    "RepairDetails": repairDetailsFields,
                    "RepairNotes": isServiceOrLeakRepair
                        ? [
                            "repairNotes",
                            "serviceActionId",
                            "repairOutcomeTypeId",
                        ]
                        : requiredFieldsRef.current.RepairNotes,
                    "InitialVerificationTest": [
                        "leakDetectionMethodId",
                        "wasLeakFound",
                    ],
                };
                // Outcome=NoRepairAttempted hides IVT/FVT — clear their required fields so this run
                // (e.g., on edit-mode mount) doesn't re-arm validation for hidden steps.
                if (formState.repairOutcomeTypeId === Enums.SE.RepairOutcomeType.NoRepairAttempted) {
                    newRequiredFields.InitialVerificationTest = [];
                    newRequiredFields.FollowUpVerificationService = [];
                }
                requiredFieldsRef.current = newRequiredFields;
                setRequiredFields(newRequiredFields);

                // Re-add InitialVerificationTest step if it was removed.
                // IVT sits before GasTransaction in the default order; fall back to before
                // Completion if GasTransaction is not present.
                const mappedSteps = serviceEventSteps.map(s => s.props.id);
                if (!mappedSteps.includes('InitialVerificationTest')) {
                    const newSteps = [...serviceEventSteps];
                    const gasIndex = mappedSteps.indexOf('GasTransaction');
                    const completionIndex = mappedSteps.indexOf('Completion');
                    const insertAt = gasIndex !== -1 ? gasIndex : completionIndex;
                    if (insertAt === -1) {
                        newSteps.push(Translate('InitialVerificationTest'));
                    } else {
                        newSteps.splice(insertAt, 0, Translate('InitialVerificationTest'));
                    }
                    setServiceEventSteps(newSteps);
                }
            } else {
                // Leak set to No: clear the dependent conditional values (Date Leak Found, Leak
                // Location Category/Location, Cause of Leak, Estimated Leak Amount) so no orphaned
                // data persists and the fields re-render empty if the user toggles back to Yes
                // (ADO #15215).
                deleteInitialLeakInspectionValues();
                const resetRequiredFields = {
                    ...requiredFieldsRef.current,
                    "RepairDetails": [
                        "leakDetectionMethodId",
                        "wasLeakFound"
                    ],
                    "RepairNotes": isServiceOrLeakRepair
                        ? [
                            "repairNotes",
                            "serviceActionId",
                        ]
                        : requiredFieldsRef.current.RepairNotes,
                    "InitialVerificationTest": [],
                    "FollowUpVerificationService": [],
                };
                requiredFieldsRef.current = resetRequiredFields;
                setRequiredFields(resetRequiredFields);
            }
        }
        if (!leakFound) {
            deleteAllLeakRelatedValues();
        }
        return setGlobalState("initialLeakInspection.wasLeakFound", leakFound, nestedProperty);
    };

    const getLeakFoundOptions = () => {
        const coolingApplianceUnderRepair = coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.UnderRepair;
        if (coolingApplianceUnderRepair && formState.workItemTypeId === Enums.SE.WorkItemType.LeakInspection) {
            return yes_no_options.filter(o => o.id);
        }
        return yes_no_options;
    };

    const getLeakFoundButtonOptions = () => getLeakFoundOptions().map(option => ({
        value: option.id,
        label: option.label ? option.label : option.name,
    }));

    const handleLeakLocationCategory = (new_leak_location_category) => {
        if (isMaintenanceNoGas) {
            setGlobalState("maintenanceNoGas.leakLocationId", null, isNestedProperty);
            return setGlobalState("maintenanceNoGas.leakLocationCategoryId", new_leak_location_category, isNestedProperty);
        }

        setGlobalState("initialLeakInspection.leakLocationId", null, isNestedProperty);
        setGlobalState("initialLeakInspection.leakLocationCategoryId", new_leak_location_category, isNestedProperty);
    };

    const handleLeakLocation = ({ leakLocationId, leakLocationCategoryId }) => {
        setGlobalState("initialLeakInspection.leakLocationId", leakLocationId, isNestedProperty);
        setGlobalState("initialLeakInspection.leakLocationCategoryId", leakLocationCategoryId, isNestedProperty);
    };

    useEffect(() => {
        setConditionalRequiredFields(formState.initialLeakInspection.wasLeakFound, isNestedProperty);
    }, []);

    if (isMaintenanceNoGas) {
        return (
            <Grid
                container
                justifyContent='center'
                spacing={4}
            >
                <Grid item xs={6} md={6} lg={6}>
                    <Select
                        label={TranslateW("ServiceEventRepairDetails.SectionRepaired")}
                        value={formState.maintenanceNoGas.wasSectionRepaired}
                        onChange={e => setGlobalState("maintenanceNoGas.wasSectionRepaired", e.target.value, isNestedProperty)}
                        error={maintenanceNoGasErrors.wasSectionRepaired}
                        options={yes_no_options}
                    />
                    <Box mt={4} />
                    <Select
                        required
                        showNullOption
                        label={TranslateW("ServiceEventRepairDetails.ProblemOrIssue") }
                        value={formState.maintenanceNoGas.leakLocationCategoryId}
                        onChange={e => handleLeakLocationCategory(e.target.value)}
                        error={maintenanceNoGasErrors.leakLocationCategoryId}
                        options={dropdownOptions.leakLocationCategories}
                    />
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                    <Input
                        fullWidth
                        multiLine
                        rows={5}
                        label={TranslateW('generalService.parts-required') }
                        value={formState.maintenanceNoGas.partsRequired}
                        onChange={e => setGlobalState("maintenanceNoGas.partsRequired", e.target.value, isNestedProperty)}
                    />
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                    <Select
                        label={TranslateW("ServiceEventRepairDetails.ProblemOrIssue")}
                        value={formState.maintenanceNoGas.leakLocationId}
                        onChange={e => setGlobalState("maintenanceNoGas.leakLocationId", e.target.value, isNestedProperty)}
                        error={maintenanceNoGasErrors.leakLocationId}
                        options={dropdownOptions.leakLocations.filter(ll => ll.category === formState.maintenanceNoGas.leakLocationCategoryId)}
                        disabled={!formState.maintenanceNoGas.leakLocationCategoryId}
                    />
                </Grid>
                <Grid item xs={6} md={6} lg={6}>
                    <Input
                        fullWidth
                        multiLine
                        rows={5}
                        required
                        label={TranslateW("leakRepair.repair-notes") }
                        value={formState.maintenanceNoGas.repairNotes}
                        onChange={e => setGlobalState("maintenanceNoGas.repairNotes", e.target.value, isNestedProperty)}
                        error={maintenanceNoGasErrors.repairNotes}
                    />
                </Grid>
            </Grid>
        );
    }

    if (isRecordRefrigerant) {
        return (
            <Grid container spacing={4} justifyContent='center'>
                <Grid item xs={12} md={12} lg={12}>
                    <Input
                        required
                        fullWidth
                        multiLine
                        label={TranslateW("retrofitView.notes")}
                        value={formState.initialLeakInspection.notes}
                        onChange={e => setGlobalState("initialLeakInspection.notes", e.target.value, isNestedProperty)}
                        {
                            ...('notes' in initialLeakInspectionErrors && {
                                error: initialLeakInspectionErrors.notes
                            })
                        }
                    />
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container spacing={4} justifyContent='center'>
            {(isServiceOrLeakRepair || isLeakInspection) && (
                <>
                    <Grid item xs={12} md={6}>
                        <Select
                            required
                            label={TranslateW("leakInspectionView.leak-detection-method") }
                            value={formState.initialLeakInspection.leakDetectionMethodId}
                            onChange={e => setGlobalState("initialLeakInspection.leakDetectionMethodId", e.target.value, isNestedProperty)}
                            error={initialLeakInspectionErrors.leakDetectionMethodId}
                            options={dropdownOptions.leakDetectionMethods}
                        />
                    </Grid>
                    <Grid item xs={false} md={6} />
                    <Grid item xs={12}>
                        <BinaryButtonGroup
                            id="initialLeakInspectionWasLeakFound"
                            required
                            label={TranslateW("leakOrCircuit") }
                            value={formState.initialLeakInspection.wasLeakFound}
                            onChange={selectedValue => setConditionalRequiredFields(selectedValue, isNestedProperty)}
                            options={getLeakFoundButtonOptions()}
                            error={initialLeakInspectionErrors.wasLeakFound}
                        />
                    </Grid>
                    {wasLeakFound && (
                        <>
                            <Grid item xs={12} md={4} lg={4}>
                                <DateInput
                                    showTodayButton
                                    allowPast
                                    allowFuture={false}
                                    label={`${TranslateW("leakLocationAndCause.date-leak-found")}*`}
                                    date={formState.initialLeakInspection.inspectionDate}
                                    onChange={new_date => setGlobalState("initialLeakInspection.inspectionDate", new_date, isNestedProperty)}
                                    error={initialLeakInspectionErrors.inspectionDate || errorExists}
                                    customHelperText={helperText}
                                />
                            </Grid>
                            <Grid item xs={12} md={8} lg={8}>
                                <LeakLocationAutocomplete
                                    required
                                    label={TranslateW("leakLocationAndCause.leak-location")}
                                    leakLocations={dropdownOptions.leakLocations}
                                    leakLocationCategories={dropdownOptions.leakLocationCategories}
                                    leakLocationId={formState.initialLeakInspection.leakLocationId}
                                    onChange={handleLeakLocation}
                                    error={initialLeakInspectionErrors.leakLocationId}
                                    helperText={initialLeakInspectionErrors.leakLocationId ? defaultHelperText : ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={6}>
                                <SearchableDropdown
                                    required
                                    label={TranslateW("leakLocationAndCause.cause-of-leak-for-asset") }
                                    value={formState.initialLeakInspection.faultCauseTypeId}
                                    onChange={id => setGlobalState("initialLeakInspection.faultCauseTypeId", id, isNestedProperty)}
                                    error={initialLeakInspectionErrors.faultCauseTypeId}
                                    options={dropdownOptions.leakCauses}
                                    searchFields={['name']}
                                />
                            </Grid>
                            <Grid container spacing={2} item xs={12} md={6} lg={6}>
                                <WeightInput
                                    showConversionArrows
                                    removePadding
                                    columnSpan={5}
                                    header={TranslateW("EstimatedLeakAmount")}
                                    variant="standard"
                                    value={formState.initialLeakInspection.estimatedLeakAmount}
                                    onChange={new_weight => setGlobalState("initialLeakInspection.estimatedLeakAmount", new_weight, isNestedProperty)}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12} md={12} lg={12}>
                        <Input
                            fullWidth
                            multiLine
                            label={TranslateW("retrofitView.notes") }
                            value={formState.initialLeakInspection.notes}
                            onChange={e => setGlobalState("initialLeakInspection.notes", e.target.value, isNestedProperty)}
                            onFocus={() => setWarning(true)}
                            onBlur={() => {
                                if (formState.initialLeakInspection.notes === "") {
                                    setWarning(false);
                                }
                            }}
                        />
                    </Grid>
                    {(isServiceOrLeakRepair && wasLeakFound) && (
                        <Grid item xs={12} md={12} lg={12}>
                            <Collapse in={warning}>
                                <InformationAlert
                                    title={TranslateW("Information")}
                                    message={TranslateW("ActualGasSection")}
                                    onClose={() => setWarning(false)}
                                />
                            </Collapse>
                        </Grid>
                    )}
                    {isLeakInspection && (
                        <Grid item xs={12} md={12} lg={12}>
                            <Collapse in={warning}>
                                <InformationAlert
                                    title={TranslateW("Information")}
                                    message={TranslateW("CertifyApplianceInspection") }
                                    onClose={() => setWarning(false)}
                                />
                            </Collapse>
                        </Grid>
                    )}
                </>
            )}
        </Grid>
    );
};

export default RepairDetails;
